import { HttpHeaders } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MapService } from '@ebw-monorepo/map';
import { PERMISSION_NAME, PermissionService, UtilsService } from '@ebw-monorepo/utils';
import { WMSCapabilities, WMTSCapabilities } from 'ol/format';
import { Subscription, debounceTime } from 'rxjs';
import { DynamicLayersFormService } from '../../../../services/dynamic-layers-form.service';

/**
 * component to handle the form to create a new layer from a service
 */
@Component({
  selector: 'ebw-monorepo-form-layers-modal',
  templateUrl: './form-layers-modal.component.html',
  styleUrls: ['./form-layers-modal.component.scss']
})
export class FormLayersModalComponent implements OnInit, OnDestroy {
  // form object
  layersForm: FormGroup;
  // variable to store the type of service selected
  serviceType: string = null;

  // parsers for WMS and WMTS capabilities
  wmsParser = new WMSCapabilities();
  wmtsParser = new WMTSCapabilities();

  // object storing the capabilities of the currently selected service
  currentCapabilities: Object = null;
  // list of available matrix sets in WMTS capabilities
  availableTileMatrixSets: string[] = [];

  // list of possible types of layers to add
  typeList: string[] = ['WMS', 'WMTS', 'XYZ', 'Vector'];

  // flag to keep track of whether the url is valid to download layer capabilities
  urlIsValid: boolean = false;

  // variables to keep track of the subscriptions to the various listeners
  urlSubscription: Subscription;
  listUpdateSubscription: Subscription;

  // local reference to the map of forms
  formsList: Map<string, FormGroup>;

  publicPermission: boolean;

  /**
   * constructor method
   * @param dialogRef reference to the popup menu
   * @param _utilsService service used to make HTTP calls
   * @param _dynamicLayersFormService service used to handle dynamic layers form related operations
   * @param data injected data coming from the component parent of this dialog
   */
  constructor(
    public dialogRef: MatDialogRef<FormLayersModalComponent>,
    private _utilsService: UtilsService,
    private _dynamicLayersFormService: DynamicLayersFormService,
    private _mapService: MapService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _permissionService: PermissionService
  ) {}

  /**
   * On Init function
   */
  ngOnInit(): void {
    // initialize the form object
    this.initLayerForm();

    this.publicPermission = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.VISIBILITY_THIRDPARTY_LAYER);

    // subscribe to changes on the forms coming from the dynamic layers form service
    this.listUpdateSubscription = this._dynamicLayersFormService.formListUpdate$.subscribe((formsList) => {
      // store the newly updated forms
      this.formsList = formsList;
    });
  }

  /**
   * On destroy lifecycle hook
   */
  ngOnDestroy(): void {
    // unsubscribe from the forms update subscription
    this.listUpdateSubscription.unsubscribe();
  }

  /**
   * method to initialize the form object
   */
  initLayerForm() {
    // create a new form object
    this.layersForm = new FormBuilder().group({
      serviceType: new FormControl(null, [Validators.required]), // control for the type of service the user is adding
      url: new FormControl('', [Validators.required]), // control for the url of the service
      visibility: new FormControl(false)
    });
  }

  /**
   * method to create new controls dynamically upon changing the type of service, and setup the correct listeners
   * @param event event object containing information about the chosen service
   */
  onChangeType(event) {
    // return in case there's no event available
    if (!event) {
      return;
    }

    // store the newly selected service type in the service (which also clears the service)
    this._dynamicLayersFormService.serviceType = event.value;

    // store the user choice for the service type
    this.serviceType = event.value;

    // pattern to test if a url is valid
    let urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;

    // define the behavior depending on the selected service type
    switch (this.serviceType) {
      // layer WMS
      case 'WMS':
        // unsubscribe to previous URL changes subscriptions
        if (this.urlSubscription) {
          this.urlSubscription.unsubscribe();
        }
        // reset the url to invalid
        this.urlIsValid = false;
        // reset the url and title controls
        this.layersForm.controls.url.reset();
        // subscribe to changes on the url control
        this.urlSubscription = this.layersForm.controls.url.valueChanges.pipe(debounceTime(500)).subscribe((url) => {
          if (!url) {
            // set the url
            this.urlIsValid = false;
            return;
          }
          // test the url against the patter to verify that it's a valid URL string
          if (!urlPattern.test(url)) {
            // set the url control to invalid
            this.layersForm.controls.url.setErrors({ malformed: true });
            // set the url
            this.urlIsValid = false;
            return;
          }

          // add a the parameters required to get the capabilities of the service to the URL
          url += `&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetCapabilities`;
          url = this._mapService.proxyUrl + url;
          // specify in the HTTP request that we're interested in an XML file
          const headers = new HttpHeaders({ Accept: 'text/xml' });
          const request = this._utilsService.getRequest(url, {}, { headers, responseType: 'text' });
          // make an HTTP request to the service to get the capabilities
          request.subscribe({
            next: (response) => {
              // notify that the url is not verified and valid
              this.urlIsValid = true;
              // clear the dynamic layers form service when the capabilities are evaluated
              this._dynamicLayersFormService.clear();
              // parse the response with the correct parser
              const result = this.wmsParser.read(response);
              // store the capabilities in the service (this also extracts the necessary data)
              this._dynamicLayersFormService.capabilities = result;
              // add a new form to the service
              this._dynamicLayersFormService.addForm();
            },
            error: (error) => {
              // notify that the url is invalid
              this.urlIsValid = false;
              // manually set the url control error, to show the user that the url is invalid
              this.layersForm.controls.url.setErrors({ notFound: true });
              console.error('[LayerForm][GetWMS]', error);
            }
          });
        });
        break;
      // layer WMTS
      case 'WMTS':
        // unsubscribe to previous URL changes subscriptions
        if (this.urlSubscription) {
          this.urlSubscription.unsubscribe();
        }
        // set the url validity flag to false
        this.urlIsValid = false;
        // reset the url and title controls
        this.layersForm.controls.url.reset();
        // subscribe to changes on the url control
        this.urlSubscription = this.layersForm.controls.url.valueChanges.pipe(debounceTime(500)).subscribe((url) => {
          if (!url) {
            // set the url
            this.urlIsValid = false;
            return;
          }
          // test the url against the patter to verify that it's a valid URL string
          if (!urlPattern.test(url)) {
            // set the url control to invalid
            this.layersForm.controls.url.setErrors({ malformed: true });
            // set the url
            this.urlIsValid = false;
            return;
          }

          // add a the parameters required to get the capabilities of the service to the URL
          url += `&SERVICE=WMTS&REQUEST=GetCapabilities`;
          url = this._mapService.proxyUrl + url;
          // specify in the HTTP request that we're interested in an XML file
          const headers = new HttpHeaders({ Accept: 'text/xml' });
          // make an HTTP request to the service to get the capabilities
          const request = this._utilsService.getRequest(url, {}, { headers: headers, responseType: 'text' });
          request.subscribe({
            next: (response) => {
              // notify that the url contains valid capabilities
              this.urlIsValid = true;
              // clear the service from previous forms
              this._dynamicLayersFormService.clear();
              // parse the response with the correct parser
              const result = this.wmtsParser.read(response);
              // store the obtained capabilities in the service (which also extracts the necessary data)
              this._dynamicLayersFormService.capabilities = result;
              // add a new form component
              this._dynamicLayersFormService.addForm();
            },
            error: (error) => {
              // notify that the url is invalid
              this.urlIsValid = false;
              // manually set the url control error, to show the user that the url is invalid
              this.layersForm.controls.url.setErrors({ incorrect: true });
              console.error('[LayerForm][GetWMTS]', error);
            }
          });
        });

        break;
      // layer Vector and XYZ
      case 'Vector':
      case 'XYZ':
        // unsubscribe to previous URL changes subscriptions
        if (this.urlSubscription) {
          this.urlSubscription.unsubscribe();
        }

        // set the url as valid by default
        this.urlIsValid = true;
        // reset the url form
        this.layersForm.controls.url.reset();
        // add a new form object and component
        this._dynamicLayersFormService.addForm();

        break;
      default:
        break;
    }
  }

  /**
   * method called upon saving the new layer
   */
  async save() {
    // call the save method of the dynamic layers form service, which will return the save status depending on the operation
    let result = await this._dynamicLayersFormService.save(this.layersForm);
    if (!result || result == 'ERROR') {
      return;
    }

    this.layersForm.reset();

    // return the result of the operation to the parent component that called this dialog
    this.dialogRef.close(result);
  }

  /**
   * wrapper method to call the service function to add a new form object and component
   */
  addForm() {
    this._dynamicLayersFormService.addForm();
  }

  isSaveEnabled() {
    return this._dynamicLayersFormService.checkValidForm(this.layersForm);
  }
}
